import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getDetailedErrorMessage } from '@/lib/errorParser';
import { useErrorReporting } from './useErrorReporting';
import { useAuth } from './useAuth';

// Refresh when the Microsoft token has less than this left.
const REFRESH_WINDOW_MS = 15 * 60 * 1000;
// If a refresh fails without disconnecting, don't retry faster than this.
const MIN_RECHECK_MS = 60 * 1000;
// setTimeout overflows above 2^31-1 ms.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export const useSharePointAuth = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(null);
  const { toast } = useToast();
  const { reportError } = useErrorReporting();
  // The user comes from AuthContext; no GET /auth/v1/user per check.
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const checkInFlightRef = useRef(false);

  // toast and reportError get a new identity on every render. Keep them out of
  // checkConnection's deps: otherwise the effect below re-runs after every state
  // update and polls Supabase in a tight loop (28-09-2026: ~12 req/s per tab).
  const toastRef = useRef(toast);
  const reportErrorRef = useRef(reportError);
  toastRef.current = toast;
  reportErrorRef.current = reportError;

  // A fresh Date with the same time must not trigger a re-render.
  const setExpiry = useCallback((next: Date | null) => {
    setTokenExpiresAt((prev) =>
      (prev?.getTime() ?? null) === (next?.getTime() ?? null) ? prev : next
    );
  }, []);

  const checkConnection = useCallback(async (silent = false) => {
    // focus and visibilitychange fire together; one silent check is enough
    if (silent && checkInFlightRef.current) return;
    checkInFlightRef.current = true;
    try {
      console.groupCollapsed('[SharePoint Auth] Checking connection...');
      if (!userId) {
        console.log('No user found');
        setIsConnected(false);
        setExpiry(null);
        console.groupEnd();
        return;
      }

      // Only the expiry comes to the browser; the tokens stay server-side.
      const { data, error } = await supabase
        .from('user_settings')
        .select('microsoft_token_expiry')
        .eq('user_id', userId)
        .not('microsoft_refresh_token', 'is', null)
        .maybeSingle();

      if (error || !data) {
        console.log('No refresh token found - disconnected');
        setIsConnected(false);
        setExpiry(null);
        console.groupEnd();
        return;
      }

      const expiry = new Date(data.microsoft_token_expiry);
      const now = new Date();
      const minutesUntilExpiry = (expiry.getTime() - now.getTime()) / 1000 / 60;

      console.log('Token status:', {
        expiresAt: expiry.toISOString(),
        minutesLeft: minutesUntilExpiry.toFixed(1),
        needsRefresh: minutesUntilExpiry <= 15
      });

      // Proactively refresh if expired or less than 15 minutes valid
      if (minutesUntilExpiry <= 15) {
        console.log('Token expired or expires soon - refreshing...');
        try {
          const { data: refreshData, error: refreshError } = await supabase.functions.invoke('sharepoint-refresh-token');
          
          if (refreshError) {
            console.error('Refresh failed:', refreshError);
            
            // Check if it's invalid_grant (refresh token revoked)
            if (refreshError.message?.includes('invalid_grant') || refreshError.message?.includes('AADSTS')) {
              console.log('Refresh token invalid - marking as disconnected');
              setIsConnected(false);
              setExpiry(null);
              
              if (!silent) {
                toastRef.current({
                  title: 'SharePoint toegang ingetrokken',
                  description: 'Je Microsoft-toegang is verlopen. Koppel opnieuw.',
                  variant: 'destructive'
                });
              }
              console.groupEnd();
              return;
            }
            
            throw refreshError;
          }

          console.log('Token refreshed successfully');
          
          // Fetch updated expiry
          const { data: updatedData } = await supabase
            .from('user_settings')
            .select('microsoft_token_expiry')
            .eq('user_id', userId)
            .single();
          
          if (updatedData?.microsoft_token_expiry) {
            const newExpiry = new Date(updatedData.microsoft_token_expiry);
            setExpiry(newExpiry);
            setIsConnected(true);
            console.log('New expiry:', newExpiry.toISOString());
          }
        } catch (refreshError: any) {
          console.error('Auto-refresh failed:', refreshError);
          
          // Only disconnect if it's a fatal error, otherwise keep trying
          if (refreshError.message?.includes('invalid_grant') || refreshError.message?.includes('No refresh token')) {
            setIsConnected(false);
            setExpiry(null);
          } else {
            // Token might still work, keep connected status
            setIsConnected(true);
            setExpiry(expiry);
          }
        }
      } else {
        // Token is still valid
        setIsConnected(true);
        setExpiry(expiry);
        console.log('Token valid - connected');
      }
      
      console.groupEnd();
    } catch (error: any) {
      console.error('Error checking SharePoint connection:', error);
      setIsConnected(false);
      setExpiry(null);
      console.groupEnd();
      
      if (!silent) {
        await reportErrorRef.current(
          'SharePoint connection check failed',
          error,
          { action: 'check_sharepoint_connection' }
        );
      }
    } finally {
      checkInFlightRef.current = false;
      setLoading(false);
    }
  }, [userId, setExpiry]);

  // One check per mount (and per signed-in user). No focus or interval polling.
  useEffect(() => {
    if (authLoading) return;
    checkConnection(true);
  }, [authLoading, checkConnection]);

  // Token keeper: wake up once, just before the refresh window opens.
  useEffect(() => {
    if (!tokenExpiresAt) return;
    const delay = Math.min(
      Math.max(tokenExpiresAt.getTime() - REFRESH_WINDOW_MS - Date.now(), MIN_RECHECK_MS),
      MAX_TIMEOUT_MS
    );
    const timer = setTimeout(() => {
      console.log('[SharePoint Token Keeper] Token expires soon - checking connection');
      checkConnection(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [tokenExpiresAt, checkConnection]);

  const initiateAuth = async () => {
    try {
      // Fetch SharePoint config from edge function
      const { data: config, error: configError } = await supabase.functions.invoke('get-sharepoint-config');
      
      if (configError || !config) {
        throw new Error('Could not fetch SharePoint configuration');
      }

      const { clientId, tenantId, sharepointRedirect } = config;
      const redirectUri = sharepointRedirect;

      // Simple state parameter (no JWT)
      const state = Math.random().toString(36);

      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_mode=query&` +
        `state=${encodeURIComponent(state)}&` +
        `scope=${encodeURIComponent('https://graph.microsoft.com/Files.ReadWrite.All https://graph.microsoft.com/Sites.ReadWrite.All offline_access')}`;

      console.log('Opening SharePoint auth window with URL:', authUrl);

      const authWindow = window.open(authUrl, 'SharePoint Auth', 'width=600,height=700');

      // Listen for messages from the callback window
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'sharepoint-auth-success') {
          console.log('SharePoint auth successful');
          window.removeEventListener('message', messageHandler);
          checkConnection();
          toast({
            title: 'SharePoint verbonden',
            description: 'Je SharePoint account is succesvol gekoppeld'
          });
        } else if (event.data.type === 'sharepoint-auth-error') {
          console.error('SharePoint auth error:', event.data.error);
          window.removeEventListener('message', messageHandler);
          toast({
            title: 'SharePoint koppeling mislukt',
            description: event.data.error,
            variant: 'destructive'
          });
        }
      };

      window.addEventListener('message', messageHandler);

      // Fallback: check if window closed
      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            window.removeEventListener('message', messageHandler);
            checkConnection();
          }
        } catch (e) {
          // Cross-origin error, ignore
        }
      }, 1000);
    } catch (error: any) {
      console.error('Error initiating SharePoint auth:', error);
      
      const detailedMessage = getDetailedErrorMessage(error);
      
      toast({
        title: 'SharePoint authenticatie mislukt',
        description: detailedMessage,
        variant: 'destructive'
      });
      
      await reportError(
        'SharePoint auth initiation failed',
        error,
        { action: 'initiate_sharepoint_auth' }
      );
    }
  };

  const disconnect = async () => {
    try {
      if (!userId) return;

      const { error } = await supabase
        .from('user_settings')
        .update({
          microsoft_access_token: null,
          microsoft_refresh_token: null,
          microsoft_token_expiry: null
        })
        .eq('user_id', userId);

      if (error) throw error;

      setIsConnected(false);
      setExpiry(null);
      toast({
        title: 'SharePoint ontkoppeld',
        description: 'Je SharePoint verbinding is verwijderd'
      });
    } catch (error: any) {
      console.error('Error disconnecting SharePoint:', error);
      
      const detailedMessage = getDetailedErrorMessage(error);
      
      toast({
        title: 'SharePoint ontkoppelen mislukt',
        description: detailedMessage,
        variant: 'destructive'
      });
      
      await reportError(
        'SharePoint disconnect failed',
        error,
        { action: 'disconnect_sharepoint' }
      );
    }
  };

  return {
    isConnected,
    loading,
    tokenExpiresAt,
    initiateAuth,
    disconnect,
    checkConnection: () => checkConnection(false)
  };
};